import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { initVersionedSchema } from '@principle-theorem/editor';
import {
  EmptyStateComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  InputSearchFilter,
  NgCommonModule,
  SearchFieldComponent,
  toSearchStream,
  TrackByFunctions,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  Patient,
  PatientDocument,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  PatientRelationshipType,
  type IPatient,
  type IPatientDocument,
} from '@principle-theorem/principle-core/interfaces';
import {
  addDoc,
  filterUndefined,
  HISTORY_DATE_FORMAT,
  isWithRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { BehaviorSubject, combineLatest, of, type Observable } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { PatientsFacade } from '../../store/facades/patients.facade';
import {
  CreatePatientDocumentDialogComponent,
  type ICreatePatientDocumentResponse,
} from '../create-patient-document-dialog/create-patient-document-dialog.component';

@Component({
  selector: 'pr-patient-documents',
  templateUrl: './patient-documents.component.html',
  styleUrls: ['./patient-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    RouterModule,
    EmptyStateComponent,
    MatButtonModule,
    SearchFieldComponent,
    MatProgressBarModule,
    MatListModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatIconModule,
  ],
})
export class PatientDocumentsComponent {
  trackByDocument = TrackByFunctions.ref<WithRef<IPatientDocument>>();
  documents$: Observable<WithRef<IPatientDocument>[]>;
  searchCtrl = new TypedFormControl<string>('');
  patient$: Observable<WithRef<IPatient>>;
  searchFilter: InputSearchFilter<WithRef<IPatientDocument>>;
  emptyState$: Observable<boolean>;
  loading$ = new BehaviorSubject<boolean>(true);
  readonly dateFormat = HISTORY_DATE_FORMAT;

  patient = input<WithRef<IPatient>>();
  displayMode = input<'inline' | 'menuItem'>('inline');
  iconName = input<string>('article');
  resetOnSelection = input<boolean>(false);
  documentSelected = output<WithRef<IPatientDocument>>();
  menuOpened = output<void>();
  menuTrigger = viewChild(MatMenuTrigger);

  constructor(
    private _patient: PatientsFacade,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _org: OrganisationService,
    private _router: Router
  ) {
    this.patient$ = toObservable(this.patient).pipe(
      debounceTime(0),
      switchMap((patientInput) =>
        patientInput
          ? of(patientInput)
          : this._patient.currentPatient$.pipe(
              map((patient) => (isWithRef(patient) ? patient : undefined)),
              filterUndefined()
            )
      )
    );

    this.documents$ = this.patient$.pipe(
      tap(() => this.loading$.next(true)),
      switchMap((patient) =>
        Patient.withPatientRelationships$(
          patient,
          [PatientRelationshipType.DuplicatePatient],
          Patient.patientDocuments$
        )
      ),
      tap(() => this.loading$.next(false))
    );

    this.searchFilter = new InputSearchFilter<WithRef<IPatientDocument>>(
      this.documents$,
      toSearchStream(this.searchCtrl),
      ['name']
    );

    this.emptyState$ = combineLatest([this.documents$, this.loading$]).pipe(
      map(([documents, loading]) => !documents.length && !loading)
    );
  }

  async createDocument(): Promise<void> {
    const patient = await snapshot(this.patient$);
    const response = await this._dialog
      .open<
        CreatePatientDocumentDialogComponent,
        WithRef<IPatient>,
        ICreatePatientDocumentResponse
      >(
        CreatePatientDocumentDialogComponent,
        DialogPresets.small({
          data: patient,
        })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    const staffer = await snapshot(this._org.staffer$.pipe(filterUndefined()));
    const content = response.template?.content ?? initVersionedSchema();

    const patientDocument = PatientDocument.init({
      name: response.name,
      content,
      createdBy: stafferToNamedDoc(staffer),
    });

    const doc = await addDoc(
      Patient.patientDocumentCol(patient),
      patientDocument
    );
    this._snackBar.open('Patient Document Created');
    await this._router.navigate([this._router.url, doc.id]);
  }

  documentClicked(document: WithRef<IPatientDocument>): void {
    this.documentSelected.emit(document);
    if (this.resetOnSelection()) {
      this.searchCtrl.setValue('');
    }
  }

  closeMenu(): void {
    this.menuTrigger()?.closeMenu();
  }
}
